import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { PaginationFintechInterface } from '../types/cashAdvanceInterface';
import {
  ActiveRowInterface,
  InstaWageFilterInterface,
  InstaWageTableDataInterface,
  InstaWageTransactionDetailsInterface,
} from '../types/instaWageInterface';
import { GetTransactionList } from '../services/InstaWage/GetTransactionList';
import { isDateBefore } from '../helpers/dateParser';

export const useInstaWageStore = defineStore('instaWageStore', () => {
  const instaWageAccess = ref<boolean>(false);
  const dashboardAccessType = ref<number>(0);
  const searchTransactionList = ref<string | null>(null);
  const showSelectedFieldsLength = ref<boolean>(false);
  const tempSelectedFieldsLength = ref<number>(0);
  const transactionFiltersDrawer = ref<boolean>(false);
  const searchTransactionListOnEnter = ref<string | null>(null);
  const isTransactionListLoading = ref<boolean>(false);
  const isTransactionListCardsLoading = ref<boolean>(false);
  const isTransactionListSearchLoading = ref<boolean>(false);
  const currentActivity = ref<string>('');
  const closeBanner = ref<boolean>(false);

  const instaWageTransactionDetails = ref<InstaWageTransactionDetailsInterface>(
    {
      state: false,
      isLoading: false,
    }
  );

  const activeRow = ref<ActiveRowInterface>({
    index: null,
    details: {
      firstName: 'string',
      lastName: 'string',
      requestedAmount: 0,
      requestStatus: 'string',
      bankName: 'string',
      bankAccountNumber: 'string',
      dateRequested: '2023-09-07T11:42:33.177Z',
      dateCredited: '2023-09-07T11:42:33.177Z',
      convenienceFee: 0,
      totalAmountDeducted: 0,
      referenceId: 'string',
      transactionId: 'string',
    },
  });

  const paginationFintech = ref<PaginationFintechInterface>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const selectedFields = ref<InstaWageFilterInterface>({
    datePicker: [{ fromDate: '', toDate: '' }],
    requestStatus: [],
    employeeStatus: [],
  });

  const selectedFieldsToFilter = ref<InstaWageFilterInterface>({
    datePicker: [{ fromDate: '', toDate: '' }],
    requestStatus: [],
    employeeStatus: [],
  });

  const tempSelectedFields = ref<InstaWageFilterInterface>({
    datePicker: [{ fromDate: '', toDate: '' }],
    requestStatus: [],
    employeeStatus: [],
  });

  const transactionTableData = ref<InstaWageTableDataInterface>({
    header: {
      clientId: 0,
      companyId: 0,
      clientName: '',
      companyName: '',
      outstandingBalance: 0,
      pending: 0,
      inProgress: 0,
      success: 0,
      paid: 0,
      failed: 0,
      totalRequests: 0,
      deducted: 0,
    },
    data: [],
    pagination: {
      currentPage: 0,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 0,
      links: {
        next: 'string',
        previous: 'string',
        last: 'string',
      },
    },
  });

  const showTransactionListLoading = () => {
    isTransactionListLoading.value = true;
  };

  const hideTransactionListLoading = () => {
    isTransactionListLoading.value = false;
  };

  const getSearch = async () => {
    searchTransactionListOnEnter.value = searchTransactionList.value;
    isTransactionListSearchLoading.value = true;
    isTransactionListCardsLoading.value = true;
    paginationFintech.value.currentPage = 1;

    if (
      searchTransactionListOnEnter.value !== null &&
      searchTransactionListOnEnter.value !== ''
    ) {
      currentActivity.value = 'search';
    }
    await getTransactionTableData();

    instaWageTransactionDetails.value.state = false;
    activeRow.value.index = null;
    activeRow.value.details = null;
  };

  const getTransactionTableData = async () => {
    try {
      const { currentPage, itemsPerPage } = paginationFintech.value;
      const { requestStatus, datePicker, employeeStatus } = selectedFieldsToFilter.value;
      const [datePickerValue] = datePicker;

      const requestStatusString = requestStatus.join(',');
      const employeeStatusString = employeeStatus.join(',');

      const response: InstaWageTableDataInterface = await GetTransactionList({
        PageNumber: currentPage,
        RowsPerPage: itemsPerPage,
        StatusIds: requestStatusString,
        EmploymentStatusGroupIds: employeeStatusString,
        DateStart: datePickerValue.fromDate,
        DateEnd: datePickerValue.toDate,
        SearchField: searchTransactionList.value,
      });
      if (typeof response !== 'object') {
        return;
      }
      transactionTableData.value = response;
      Object.assign(paginationFintech.value, response.pagination);
    } catch (error) {
      console.error(error);
    }

    isTransactionListSearchLoading.value = false;
    isTransactionListCardsLoading.value = false;
  };

  const changePage = async (paginate: PaginationFintechInterface) => {
    paginationFintech.value.currentPage = paginate.currentPage;
    paginationFintech.value.itemsPerPage = paginate.itemsPerPage;
    await getTransactionTableData();
  };

  const confirmFilter = async () => {
    currentActivity.value = 'filter';
    isTransactionListSearchLoading.value = true;
    isTransactionListCardsLoading.value = true;
    paginationFintech.value.currentPage = 1;
    selectedFieldsToFilter.value = selectedFields.value;
    await getTransactionTableData();
  };

  const resetSelectedFields = async () => {
    if (currentActivity.value === 'filter') {
      if (
        searchTransactionListOnEnter.value !== null &&
        searchTransactionListOnEnter.value !== ''
      ) {
        currentActivity.value = 'search';
      } else currentActivity.value = '';
    }
    isTransactionListSearchLoading.value = true;
    isTransactionListCardsLoading.value = true;
    paginationFintech.value.currentPage = 1;
    tempSelectedFields.value = null;
    tempSelectedFieldsLength.value = 0;
    await getTransactionTableData();
  };

  const hasNonEmptyValue = (item: string) =>
    Object.values(item).some((date) => date !== '');

  const isNonEmptyArray = (value: string[]) =>
    Array.isArray(value) && value.some(hasNonEmptyValue);

  const isNonEmptyString = (value: string) =>
    typeof value === 'string' && value.length > 0;

  const selectedFieldsLength = computed(() => {
    return Object.keys(selectedFields.value).filter((property) => {
      const value = selectedFields.value[property];
      return isNonEmptyArray(value) || isNonEmptyString(value);
    }).length;
  });

  const isValidToDate = computed(() => {
    const datePicker = selectedFields.value?.datePicker[0];
    if (datePicker?.fromDate && datePicker?.toDate) {
      return isDateBefore(datePicker.toDate, datePicker.fromDate);
    }
    return false;
  });

  return {
    instaWageAccess,
    dashboardAccessType,
    activeRow,
    instaWageTransactionDetails,
    paginationFintech,
    transactionTableData,
    getTransactionTableData,
    changePage,
    selectedFields,
    selectedFieldsToFilter,
    searchTransactionList,
    searchTransactionListOnEnter,
    getSearch,
    isValidToDate,
    transactionFiltersDrawer,
    resetSelectedFields,
    selectedFieldsLength,
    showSelectedFieldsLength,
    tempSelectedFields,
    tempSelectedFieldsLength,
    confirmFilter,
    isTransactionListLoading,
    showTransactionListLoading,
    hideTransactionListLoading,
    isTransactionListCardsLoading,
    isTransactionListSearchLoading,
    currentActivity,
    closeBanner,
  };
});
